// Public loader changelog: every release's notes, newest first, drawn as a timeline.
import { motion } from "motion/react";
import { Link } from "react-router";
import { useApi } from "../api.ts";
import { fmtDateTime, fmtRelative } from "../format.ts";
import { Icon, LoadError, Loading } from "../ui/index.tsx";
import { Markdown } from "../ui/markdown.tsx";
import { DISCORD_INVITE } from "../ui/logo.tsx";
import { PublicHeading } from "./Games.tsx";
import type { ChangelogEntry } from "../../shared/types.ts";

export default function Changelog() {
  const list = useApi<ChangelogEntry[]>("/changelog");
  const items = list.data ?? [];
  return (
    <>
      <PublicHeading title="Changelog" body="Every loader release, newest first. The loader updates itself, so you're always on the latest."
        aside={<a href={DISCORD_INVITE} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-fg"><Icon name="bell" className="size-4" />Get updates in Discord</a>} />
      <section className="mx-auto max-w-3xl px-6 py-12">
        {list.loading && !list.data ? <Loading /> : list.error && !list.data ? <LoadError error={list.error} retry={list.reload} /> : items.length === 0 ? (
          <p className="rounded-[var(--radius-card)] border border-dashed border-line py-12 text-center text-sm text-muted">No releases published yet.</p>
        ) : (
          <ol className="relative border-l border-line pl-8">
            {items.map((v, i) => (
              <motion.li key={`${v.version}-${v.created_at}`} className="relative pb-12 last:pb-0"
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "0px 0px -10% 0px" }}
                transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1], delay: i < 3 ? i * 0.08 : 0 }}>
                {/* Node on the timeline; the newest one glows and pings. */}
                <span className="absolute -left-[39px] top-1.5 grid size-3.5 place-items-center">
                  {i === 0 && <span className="absolute inset-0 animate-ping rounded-full bg-accent/60" />}
                  <span className={`relative size-3.5 rounded-full ring-4 ring-bg ${i === 0 ? "bg-accent shadow-[0_0_14px_var(--color-accent)]" : "bg-line-strong"}`} />
                </span>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <h2 className="font-display text-2xl font-semibold tracking-tight">v{v.version}</h2>
                  {i === 0 && <span className="rounded bg-accent px-1.5 text-[10px] font-semibold uppercase leading-4 text-accent-fg">Latest</span>}
                  <time className="font-mono text-xs text-faint" title={fmtDateTime(v.created_at)}>{fmtRelative(v.created_at)}</time>
                </div>
                <div className="pane mt-3 p-5">
                  {v.changelog?.trim()
                    ? <Markdown text={v.changelog} className="text-sm leading-relaxed text-muted" />
                    : <p className="text-sm text-muted">Stability and compatibility improvements.</p>}
                </div>
              </motion.li>
            ))}
          </ol>
        )}
        <p className="mt-12 text-sm text-faint">Already a member? The latest build is always on <Link to="/downloads" className="text-muted hover:text-fg">Downloads</Link>.</p>
      </section>
    </>
  );
}
